import React, { useEffect, useRef, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdSave, MdLink, MdLinkOff, MdSearch, MdPerson } from 'react-icons/md';

const COUPON_TYPES = ['fixed-margin'];

interface LinkedUser {
    _id: string;
    name: string;
    surname: string;
    email: string;
    type: string;
}

interface SearchUser {
    _id: string;
    name: string;
    surname: string;
    email: string;
    type: string;
    referal?: string;
}

interface LinkedSeller {
    _id: string;
    name: string;
    surname: string;
    email: string;
    affiliateCode: string;
}

export default function CouponForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [formData, setFormData] = useState({
        coupon: '',
        type: 'fixed-margin',
        fixedValue: 0,
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const [linkedUsers, setLinkedUsers] = useState<LinkedUser[]>([]);
    const [linkLoading, setLinkLoading] = useState(false);
    const [linkError, setLinkError] = useState('');
    const [linkSuccess, setLinkSuccess] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
    const [searching, setSearching] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    const [linkedSeller, setLinkedSeller] = useState<LinkedSeller | null>(null);
    const [sellerSearchQuery, setSellerSearchQuery] = useState('');
    const [sellerSearchResults, setSellerSearchResults] = useState<SearchUser[]>([]);
    const [sellerSearching, setSellerSearching] = useState(false);
    const [showSellerDropdown, setShowSellerDropdown] = useState(false);
    const [sellerLinkLoading, setSellerLinkLoading] = useState(false);
    const [sellerLinkError, setSellerLinkError] = useState('');
    const [sellerLinkSuccess, setSellerLinkSuccess] = useState('');
    const sellerSearchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isEdit) fetchCoupon();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (!showDropdown) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    useEffect(() => {
        if (!showSellerDropdown) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (sellerSearchRef.current && !sellerSearchRef.current.contains(e.target as Node)) {
                setShowSellerDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showSellerDropdown]);

    const fetchCoupon = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/coupons/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const c = response.data;
            setFormData({ coupon: c.coupon, type: c.type, fixedValue: c.fixedValue });
            if (c.seller) setLinkedSeller(c.seller);
            fetchLinkedUsers();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to load coupon');
        } finally {
            setLoading(false);
        }
    };

    const fetchLinkedUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/coupons/${id}/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLinkedUsers(response.data);
        } catch (e) {
            console.error('Error fetching linked users:', e);
        }
    };

    const handleSellerSearch = async (query: string) => {
        setSellerSearchQuery(query);
        if (query.trim().length < 2) {
            setSellerSearchResults([]);
            setShowSellerDropdown(false);
            return;
        }
        try {
            setSellerSearching(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/users`, {
                params: { search: query, limit: 10, type: 'seller' },
                headers: { Authorization: `Bearer ${token}` },
            });
            setSellerSearchResults(response.data.users || []);
            setShowSellerDropdown(true);
        } catch (e) {
            console.error('Error searching sellers:', e);
        } finally {
            setSellerSearching(false);
        }
    };

    const handleLinkSeller = async (seller: SearchUser) => {
        setShowSellerDropdown(false);
        setSellerSearchQuery('');
        setSellerSearchResults([]);
        setSellerLinkError('');
        setSellerLinkSuccess('');
        try {
            setSellerLinkLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.patch(
                `${CONFIG.apiUrl}/admin/coupons/${id}/seller`,
                { sellerId: seller._id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLinkedSeller(response.data.seller);
            setSellerLinkSuccess(`${seller.name} ${seller.surname} lié — code affilié mis à jour vers ${formData.coupon}.`);
        } catch (e: any) {
            setSellerLinkError(e.response?.data?.message || 'Erreur lors de la liaison');
        } finally {
            setSellerLinkLoading(false);
        }
    };

    const handleUnlinkSeller = async () => {
        setSellerLinkError('');
        setSellerLinkSuccess('');
        try {
            setSellerLinkLoading(true);
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/coupons/${id}/seller`,
                { sellerId: null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLinkedSeller(null);
            setSellerLinkSuccess('Seller délié du coupon.');
        } catch (e: any) {
            setSellerLinkError(e.response?.data?.message || 'Erreur lors de la désliaison');
        } finally {
            setSellerLinkLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.coupon.trim()) {
            setError('Le code est requis');
            return;
        }
        try {
            setSaving(true);
            setError('');
            const token = localStorage.getItem('token');
            if (isEdit) {
                await axios.put(`${CONFIG.apiUrl}/admin/coupons/${id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${CONFIG.apiUrl}/admin/coupons`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            navigate('/back-office/coupons');
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to save coupon');
        } finally {
            setSaving(false);
        }
    };

    const handleSearch = async (query: string) => {
        setSearchQuery(query);
        if (query.trim().length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }
        try {
            setSearching(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/users`, {
                params: { search: query, limit: 10 },
                headers: { Authorization: `Bearer ${token}` },
            });
            setSearchResults(response.data.users || []);
            setShowDropdown(true);
        } catch (e) {
            console.error('Error searching users:', e);
        } finally {
            setSearching(false);
        }
    };

    const handleLinkUser = async (user: SearchUser) => {
        setShowDropdown(false);
        setSearchQuery('');
        setSearchResults([]);
        setLinkError('');
        setLinkSuccess('');
        try {
            setLinkLoading(true);
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/users/${user._id}`,
                { referal: formData.coupon },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLinkSuccess(`${user.name} ${user.surname} (${user.email}) lié au coupon.`);
            fetchLinkedUsers();
        } catch (e: any) {
            setLinkError(e.response?.data?.message || 'Erreur lors de la liaison');
        } finally {
            setLinkLoading(false);
        }
    };

    const handleUnlinkUser = async (user: LinkedUser) => {
        setLinkError('');
        setLinkSuccess('');
        try {
            setLinkLoading(true);
            const token = localStorage.getItem('token');
            await axios.patch(
                `${CONFIG.apiUrl}/admin/users/${user._id}`,
                { referal: '' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLinkSuccess(`${user.name} ${user.surname} délié du coupon.`);
            fetchLinkedUsers();
        } catch (e: any) {
            setLinkError(e.response?.data?.message || 'Erreur lors de la désliaison');
        } finally {
            setLinkLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-5">
                    <div className="py-10 text-center text-gray-500">Loading...</div>
                </Card>
            </div>
        );
    }

    return (
        <div className="pt-[50px] flex flex-col gap-4">
            <Card extra="p-5">
                <div className="mb-5 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/back-office/coupons')}
                        className="rounded-lg bg-gray-200 p-2 text-gray-700 hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        {isEdit ? 'Modifier le coupon' : 'Nouveau coupon'}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Code *
                            </label>
                            <input
                                type="text"
                                value={formData.coupon}
                                onChange={(e) => setFormData({ ...formData, coupon: e.target.value.toUpperCase() })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 font-mono text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                placeholder="EX: PARTENAIRE2024"
                                required
                            />
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Type *
                            </label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                required
                            >
                                {COUPON_TYPES.map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Valeur fixe (€)
                            </label>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={formData.fixedValue}
                                onChange={(e) => setFormData({ ...formData, fixedValue: parseFloat(e.target.value) || 0 })}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
                        <button
                            type="button"
                            onClick={() => navigate('/back-office/coupons')}
                            className="rounded-lg border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdSave />
                            {saving ? 'Saving...' : isEdit ? 'Mettre à jour' : 'Créer'}
                        </button>
                    </div>
                </form>
            </Card>

            {isEdit && (
                <Card extra="p-5">
                    <div className="mb-4 flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
                        <MdPerson className="text-brand-500" />
                        Seller associé
                    </div>
                    <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                        Lier un seller à ce coupon met à jour son code affilié (ref sur /affiliate) vers{' '}
                        <span className="font-mono font-semibold text-navy-700 dark:text-white">{formData.coupon || '…'}</span>.
                    </p>

                    {sellerLinkError && (
                        <div className="mb-3 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            {sellerLinkError}
                        </div>
                    )}
                    {sellerLinkSuccess && (
                        <div className="mb-3 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {sellerLinkSuccess}
                        </div>
                    )}

                    {linkedSeller ? (
                        <div className="flex items-center justify-between rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 dark:border-brand-800 dark:bg-brand-900/20">
                            <div>
                                <span className="text-sm font-medium text-navy-700 dark:text-white">
                                    {linkedSeller.name} {linkedSeller.surname}
                                </span>
                                <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{linkedSeller.email}</span>
                                <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                    Code affilié actuel :{' '}
                                    <span className="font-mono">{linkedSeller.affiliateCode}</span>
                                    {linkedSeller.affiliateCode !== formData.coupon && (
                                        <span className="ml-1 text-orange-500">⚠ ne correspond pas au code coupon</span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleUnlinkSeller}
                                disabled={sellerLinkLoading}
                                className="flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                            >
                                <MdLinkOff />
                                Délier
                            </button>
                        </div>
                    ) : (
                        <div ref={sellerSearchRef} className="relative">
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Rechercher un seller
                            </label>
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                    <MdSearch className="text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={sellerSearchQuery}
                                    onChange={(e) => handleSellerSearch(e.target.value)}
                                    onFocus={() => sellerSearchResults.length > 0 && setShowSellerDropdown(true)}
                                    placeholder="Nom, prénom ou email…"
                                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                                />
                                {sellerSearching && (
                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">…</div>
                                )}
                                {showSellerDropdown && sellerSearchResults.length > 0 && (
                                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                        {sellerSearchResults.map((u) => (
                                            <button
                                                key={u._id}
                                                type="button"
                                                onClick={() => handleLinkSeller(u)}
                                                disabled={sellerLinkLoading}
                                                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-navy-700 disabled:opacity-50"
                                            >
                                                <span>
                                                    <span className="font-medium text-navy-700 dark:text-white">{u.name} {u.surname}</span>
                                                    <span className="ml-2 text-gray-500 dark:text-gray-400">{u.email}</span>
                                                </span>
                                                <span className="flex items-center gap-1 text-xs text-brand-500">
                                                    <MdLink />
                                                    Lier
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {showSellerDropdown && sellerSearchResults.length === 0 && !sellerSearching && sellerSearchQuery.length >= 2 && (
                                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 shadow-lg dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400">
                                        Aucun seller trouvé
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Card>
            )}

            {isEdit && (
                <Card extra="p-5">
                    <div className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
                        Utilisateurs liés à ce coupon
                    </div>
                    <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                        Lier un utilisateur à ce coupon remplacera son code de parrainage actuel par{' '}
                        <span className="font-mono font-semibold text-navy-700 dark:text-white">{formData.coupon || '…'}</span>.
                    </p>

                    {linkError && (
                        <div className="mb-3 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                            {linkError}
                        </div>
                    )}
                    {linkSuccess && (
                        <div className="mb-3 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {linkSuccess}
                        </div>
                    )}

                    <div className="mb-5" ref={searchRef}>
                        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Rechercher un utilisateur
                        </label>
                        <div className="relative">
                            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                <MdSearch className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                                placeholder="Nom, prénom ou email…"
                                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                            />
                            {searching && (
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
                                    …
                                </div>
                            )}
                            {showDropdown && searchResults.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-navy-600 dark:bg-navy-800">
                                    {searchResults.map((u) => (
                                        <button
                                            key={u._id}
                                            type="button"
                                            onClick={() => handleLinkUser(u)}
                                            disabled={linkLoading}
                                            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-navy-700 disabled:opacity-50"
                                        >
                                            <span>
                                                <span className="font-medium text-navy-700 dark:text-white">
                                                    {u.name} {u.surname}
                                                </span>
                                                <span className="ml-2 text-gray-500 dark:text-gray-400">{u.email}</span>
                                            </span>
                                            <span className="flex items-center gap-1 text-xs text-brand-500">
                                                <MdLink />
                                                {u.referal ? (
                                                    <span className="text-orange-500">remplace <span className="font-mono">{u.referal}</span></span>
                                                ) : (
                                                    'Lier'
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {showDropdown && searchResults.length === 0 && !searching && searchQuery.length >= 2 && (
                                <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 shadow-lg dark:border-navy-600 dark:bg-navy-800 dark:text-gray-400">
                                    Aucun utilisateur trouvé
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            Utilisateurs actuellement liés ({linkedUsers.length})
                        </div>
                        {linkedUsers.length === 0 ? (
                            <div className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-400 dark:border-navy-600">
                                Aucun utilisateur lié à ce coupon
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-navy-600 dark:border-navy-600">
                                {linkedUsers.map((u) => (
                                    <div key={u._id} className="flex items-center justify-between px-4 py-3">
                                        <div>
                                            <span className="text-sm font-medium text-navy-700 dark:text-white">
                                                {u.name} {u.surname}
                                            </span>
                                            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">{u.email}</span>
                                            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 dark:bg-navy-700 dark:text-gray-400">
                                                {u.type}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleUnlinkUser(u)}
                                            disabled={linkLoading}
                                            className="flex items-center gap-1 rounded-lg bg-red-100 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-200 disabled:opacity-50 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                                        >
                                            <MdLinkOff />
                                            Délier
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Card>
            )}
        </div>
    );
}
